// Updated to use real Firebase auth and AppStateProvider from transition-web
import { Suspense } from 'react'
import { lazyComponent } from './libs/onUnhandledPromiseRejection'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@mui/material/styles'
import { AppStateProvider } from './hooks/useAppState'
import useAppState from './hooks/useAppState'
import { SessionStateProvider } from './hooks/useAppStateSession'
import { store } from './store'
import { muiTheme } from './theme/muiTheme'
import SignIn from './components/SignIn'
import SignedIn from './components/SignedIn'
import Onboarding from './components/Onboarding'
import { RedirectToPublicSite } from './components/RedirectToPublicSite'
import { FacilitatorDashboard } from './features/facilitator/pages/FacilitatorDashboard'
import GamerDashboard from './features/gamer/pages/GamerDashboard'
import { ProtectedRoute } from './components/ProtectedRoute'
import CollaborationRedirectPage from './features/shared/pages/CollaborationRedirectPage'
import { ROUTES } from './utils/routing'
import { ActivityNotificationContainer } from './shared/components/ActivityNotification'
import { CookieConsentBannerContainer } from './shared/components/CookieConsentBanner'
import { ReduxAuthSync } from './components/ReduxAuthSync'
import { NotificationBell } from './shared/components/NotificationBell'
import { useInAppNotificationsListener } from './shared/hooks/useInAppNotificationsListener'
import { useAutoMarkExpiredGameReminders } from './shared/hooks/useAutoMarkExpiredGameReminders'
import { GlobalWherebyFloat } from './features/gamer/components/whereby/GlobalWherebyFloat'
import { WherebyReconnectOverlay } from './features/gamer/components/whereby/WherebyReconnectOverlay'
// Lazy: mounted in both shells but opened by a minority of users, and it pulls in the chat panel and
// the realtime voice stack. Keeping it out of the shell chunk costs nothing — it renders nothing until
// a user actually clicks the bubble.
const AppAssistantLauncher = lazyComponent(
  () => import('./shared/components/AppAssistant/AppAssistantLauncher'),
  'AppAssistantLauncher'
)
import { OrgTenantProvider } from './features/organization/OrgContext'
import { OrgSwitchShell } from './features/organization/orgSwitch'
import { AcceptOrgInvitePage } from './features/organization/AcceptOrgInvitePage'
import { OrgOnboardingWizard } from './features/organization/onboarding/OrgOnboardingWizard'
import { OrgAdminOnboardingRedirect } from './features/organization/OrgAdminOnboardingRedirect'
import { OrgRole } from '../../pure-js/types/OrganizationMembership'
import { PARTICIPANT_ORG_ROLES, STAFF_ORG_ROLES } from '../../pure-js/libs/organizationMembershipHelpers'
/** New dummy comment to trigger the Github workflow. */

function InAppNotificationsMount() {
  const { state } = useAppState()
  const userId = state.user?.id && state.user.id !== '' ? state.user.id : undefined
  useInAppNotificationsListener(userId)
  useAutoMarkExpiredGameReminders()
  return <NotificationBell userId={userId} />
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1
    }
  }
})

export default function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <AppStateProvider>
            <SessionStateProvider>
              <ReduxAuthSync />
              <Router>
                <OrgTenantProvider>
                  <OrgSwitchShell>
                    <AppContent />
                    <CookieConsentBannerContainer />
                    <ActivityNotificationContainer />
                    <InAppNotificationsMount />
                  </OrgSwitchShell>
                </OrgTenantProvider>
              </Router>
            </SessionStateProvider>
          </AppStateProvider>
        </Provider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

function AppContent() {
  return (
    <>
      <Routes>
        {/* Root: sign-in (public pages live on intraction.com) */}
        <Route path="/" element={<SignIn />} />

        {/* Authentication routes - public */}
        <Route path="/login" element={<SignIn />} />
        <Route path="/signedin" element={<SignedIn />} />
        <Route path="/onboarding" element={<Onboarding />} />

        {/* Public routes → redirect to public site */}
        <Route path="/terms" element={<RedirectToPublicSite path="/privacy" />} />
        <Route path="/privacy" element={<RedirectToPublicSite path="/privacy" />} />
        <Route path="/about" element={<RedirectToPublicSite path="/about" />} />
        <Route path="/knowledge-hub" element={<RedirectToPublicSite path="/knowledge-hub" />} />
        <Route path="/blog-posts" element={<RedirectToPublicSite path="/knowledge-hub" />} />

        {/* Legacy collaboration route - protected redirect */}
        <Route
          path={ROUTES.COLLABORATION}
          element={
            <ProtectedRoute allowedRoles={[...PARTICIPANT_ORG_ROLES, ...STAFF_ORG_ROLES]} routeType="shared">
              <CollaborationRedirectPage />
            </ProtectedRoute>
          }
        />

        <Route path="/invite/:inviteToken" element={<AcceptOrgInvitePage />} />

        {/* Org onboarding (new org admin from invite, status=PENDING_ONBOARDING).
            Keep this under `/org/:orgId/...` so it cannot collide with `/gamer/*`. */}
        <Route
          path="/org/:orgId/onboarding"
          element={
            <ProtectedRoute allowedRoles={[OrgRole.FACILITATOR, OrgRole.ADMIN]} routeType="facilitator">
              <OrgOnboardingWizard />
            </ProtectedRoute>
          }
        />

        {/* Primary facilitator dashboard */}
        <Route
          path="/facilitator/*"
          element={
            <ProtectedRoute allowedRoles={[OrgRole.FACILITATOR, OrgRole.ADMIN]} routeType="facilitator">
              <OrgAdminOnboardingRedirect>
                <GlobalWherebyFloat />
                <WherebyReconnectOverlay />
                <Suspense fallback={null}>
                  <AppAssistantLauncher />
                </Suspense>
                <FacilitatorDashboard />
              </OrgAdminOnboardingRedirect>
            </ProtectedRoute>
          }
        />

        {/* Primary gamer dashboard */}
        <Route
          path="/gamer/*"
          element={
            <ProtectedRoute allowedRoles={PARTICIPANT_ORG_ROLES} routeType="gamer">
              <GlobalWherebyFloat />
              <WherebyReconnectOverlay />
              <Suspense fallback={null}>
                <AppAssistantLauncher />
              </Suspense>
              <GamerDashboard />
            </ProtectedRoute>
          }
        />

        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
